import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import {Box, Button, Stack, Tooltip} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import {LocalizedText} from "crqlar-react-common/dist/models/common/localizedText.model";
import {Survey, SurveyQuestionType} from "crqlar-react-common/dist/models/survey/survey.model";
import {SurveyQuestion} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useGuestCategories} from "../../guestCategories/useGuestCategories";
import PublicSurveyFooter from "../public/PublicSurveyFooter";
import SurveySuccessAnimation from "../public/SurveySuccessAnimation";
import {isScreen} from "../shared/surveyQuestions.util";
import {
    isAnswered,
    SurveyFieldProblem,
    surveyAnswerProblem,
    surveyAnswerProblems
} from "../shared/surveyAnswerValidation.util";
import {isPagedFormat, SurveyPage, surveyPages, surveyProgress, visiblePages} from "../shared/surveyPages.util";
import {describeVisibilityRule} from "../shared/surveyVisibilityRules.util";
import {RunnerQuestionServices, RunnerSignatureCapture, SurveyAnswerDraft, SurveyRunnerLocalize} from "./questions/runnerQuestion.types";
import SurveyRunnerFrame from "./SurveyRunnerFrame";
import SurveyRunnerIntroCard from "./SurveyRunnerIntroCard";
import SurveyRunnerMarkdown from "./SurveyRunnerMarkdown";
import SurveyRunnerQuestionCard from "./SurveyRunnerQuestionCard";
import SurveyRunnerScreenTitle from "./SurveyRunnerScreenTitle";
import SurveyRunnerSectionHeading from "./SurveyRunnerSectionHeading";
import {surveyQuestionLayout} from "./surveyRunnerColors";

interface Props {
    survey: Survey
    language?: string
    onLanguageChange?: (language: string) => void
    organizationName?: string
    initialQuestionId?: string
}

/*
    The first control of a card's answer area, in tab order, the same one the guest page focuses after a failed
    Continue: never the help button in the title, and never the hidden helper inputs MUI renders beside its fields.
    The guest page (public/PublicSurveyPage.tsx) carries the same helper, and public/surveyRunnersParity.test.tsx
    holds both to this rule — change one and change this.
*/
const firstAnswerControl = (card: HTMLElement): HTMLElement | undefined => {
    const area = card.querySelector('[data-survey-answer]');
    if (!area) return undefined;

    return Array.from(area.querySelectorAll<HTMLElement>('input, select, textarea, button, [tabindex]'))
        .find(control => control.tabIndex >= 0
            && !control.hasAttribute('disabled')
            && control.getAttribute('aria-hidden') !== 'true'
            && control.getAttribute('type') !== 'hidden');
};

/*
    Answers, problems and pages are all keyed by question id. A question without one, as in a template
    preview, takes its position among the answerable questions, which is also the key surveyPages gives
    its page.
*/
const withPreviewIds = (questions: SurveyQuestion[]): SurveyQuestion[] => {
    const answerable = questions.filter(question => !isScreen(question));

    return questions.map(question => (
        question.id || isScreen(question)
            ? question
            : {...question, id: `#${answerable.indexOf(question)}`}
    ));
};

function SurveyRunner({survey, language, onLanguageChange, organizationName, initialQuestionId}: Props) {
    const activeLanguage = language ?? survey.languages?.[0] ?? 'en';

    const {t} = useTranslation(undefined, {lng: activeLanguage});

    const {t: tEditor} = useTranslation();

    const questionLayout = surveyQuestionLayout(useTheme());

    // Names the guest categories in the staff-only rule hint; the guest page never renders this runner.
    const {categories: guestCategories} = useGuestCategories();

    const localize: SurveyRunnerLocalize = useMemo(() => (text?: LocalizedText) => (
        text?.[activeLanguage] ?? ''
    ), [activeLanguage]);

    const questions = useMemo(() => withPreviewIds(survey.questions ?? []), [survey.questions]);
    const welcome = questions.find(question => question.type === SurveyQuestionType.WELCOME);
    const endScreen = questions.find(question => question.type === SurveyQuestionType.END_SCREEN);

    /*
        The guest page's pages without its visibility rules: the preview shows every question guests can get. A
        switched-off one is left out, as publishing leaves it out, and a section it opened keeps its heading.
    */
    const pages = useMemo(
        () => visiblePages(surveyPages(questions, survey.format), question => !question.switchedOff),
        [questions, survey.format]
    );
    const paged = isPagedFormat(survey.format);

    const initialPage = initialQuestionId
        ? pages.find(page => page.questions.some(question => question.id === initialQuestionId))
        : undefined;
    const initialFinished = !!initialQuestionId && endScreen?.id === initialQuestionId;

    const [started, setStarted] = useState(!!initialPage || initialFinished);
    const [pageKey, setPageKey] = useState(initialPage?.key);
    const [finished, setFinished] = useState(initialFinished);
    const [answers, setAnswers] = useState<Record<string, SurveyAnswerDraft>>({});
    const [problems, setProblems] = useState<Record<string, SurveyFieldProblem>>({});
    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const signatureCaptures = useRef<Record<string, RunnerSignatureCapture | undefined>>({});
    const services: RunnerQuestionServices = useMemo(() => ({
        registerSignatureCapture: (id, capture) => {signatureCaptures.current[id] = capture;},
    }), []);

    const captureSignatures = async () => {
        await Promise.all(Object.entries(signatureCaptures.current).map(async ([id, capture]) => {
            const mediaURI = await capture?.();
            if (!mediaURI) return;
            setAnswers(previous => ({...previous, [id]: {...previous[id], signatureValue: {mediaURI}}}));
        }));
    };

    const topRef = useRef<HTMLDivElement | null>(null);
    const mountedRef = useRef(false);
    useEffect(() => {
        if (!mountedRef.current) {
            mountedRef.current = true;
            return;
        }
        topRef.current?.scrollIntoView({block: 'start', behavior: 'auto'});
    }, [started, pageKey, finished]);

    const submitLabel = localize(endScreen?.buttonLabel) || t('Submit');
    const pageIndex = Math.max(pages.findIndex(page => page.key === pageKey), 0);
    const page: SurveyPage | undefined = pages[pageIndex];
    const onLastPage = pageIndex >= pages.length - 1;
    const onWelcomeScreen = paged && !!welcome && !started;
    // withPreviewIds gave every answerable question an id.
    const idOf = (question: SurveyQuestion) => question.id!;

    const setAnswer = (id: string, draft: SurveyAnswerDraft) => {
        setAnswers(previous => ({...previous, [id]: draft}));

        // A changed answer takes its problem with it; the next tap checks again.
        setProblems(previous => {
            if (!previous[id]) return previous;

            const next = {...previous};
            delete next[id];
            return next;
        });
    };

    // A problem is only ever found on the page on screen, so its card is already rendered: no page to change first.
    const showProblem = (question: SurveyQuestion) => {
        const card = cardRefs.current[idOf(question)];
        if (!card) return;

        card.scrollIntoView({block: 'center', behavior: 'smooth'});
        firstAnswerControl(card)?.focus({preventScroll: true});
    };

    /*
        Continue and Submit are never greyed out, as on the guest page: a tap checks the page and points at
        what is missing. A single page is one page holding every question.
    */
    const advance = async () => {
        const onPage = page?.questions ?? [];
        const found = surveyAnswerProblems(onPage, answers);
        const first = onPage.find(question => !!found[idOf(question)]);

        if (first) {
            setProblems(current => ({...current, ...found}));
            showProblem(first);
            return;
        }

        await captureSignatures();
        if (onLastPage) setFinished(true);
        else setPageKey(pages[pageIndex + 1].key);
    };

    const goBack = async () => {
        await captureSignatures();
        if (pageIndex > 0) setPageKey(pages[pageIndex - 1].key);
        else setStarted(false);
    };

    const card = (question: SurveyQuestion, position: number, titleLevel: 2 | 3) => {
        const id = idOf(question);
        const draft = answers[id];
        const problem = problems[id];
        // Only a given answer gets a live hint: the asterisk already says that an answer is required.
        const hint = isAnswered(draft) ? surveyAnswerProblem(question, draft) : undefined;

        // Staff-only hint: this runner renders the editor and template previews, never the guest page.
        const rules = question.visibilityRules ?? [];
        /*
            In Plain the rule between two questions goes above this hint, so the hint reads as part of its own
            question: the wrapper draws it instead of the card. Same rule and spacing as the card's `separated`
            (runner/SurveyRunnerQuestionCard.tsx) — change one and change this.
        */
        const ruleAboveHint = questionLayout.plain && position > 0 && !!rules.length;

        const questionCard = (
            <SurveyRunnerQuestionCard
                question={question}
                localize={localize}
                language={activeLanguage}
                value={draft ?? {}}
                onAnswerChange={next => setAnswer(id, next)}
                services={services}
                error={problem ? t(problem.key, problem.values) : undefined}
                hint={hint ? t(hint.key, hint.values) : undefined}
                showFooter
                titleLevel={titleLevel}
                separated={position > 0 && !ruleAboveHint}
            />
        );

        return (
            <Box
                key={id}
                ref={(element: HTMLDivElement | null) => {
                    cardRefs.current[id] = element;
                }}
                sx={ruleAboveHint ? {borderTop: '1px solid', borderColor: 'divider', pt: {xs: 2.5, sm: 3}} : undefined}
            >
                {!!rules.length && (
                    <Tooltip
                        title={
                            <Stack>
                                <Typography variant={'caption'}>{tEditor('Shown only if:')}</Typography>
                                {rules.map((rule, index) => (
                                    <Typography key={index} variant={'caption'}>
                                        {describeVisibilityRule(rule, questions, activeLanguage, tEditor, guestCategories)}
                                    </Typography>
                                ))}
                                <Typography variant={'caption'} sx={{mt: 0.5, fontStyle: 'italic'}}>
                                    {tEditor('In the preview, conditional questions are always shown.')}
                                </Typography>
                            </Stack>
                        }
                    >
                        <Stack
                            direction={'row'}
                            alignItems={'center'}
                            gap={0.5}
                            sx={{color: 'text.secondary', mb: 0.5, width: 'fit-content'}}
                        >
                            <FilterAltOutlinedIcon sx={{fontSize: 14}}/>
                            <Typography variant={'caption'}>{tEditor('Conditional')}</Typography>
                        </Stack>
                    </Tooltip>
                )}
                {questionCard}
            </Box>
        );
    };

    const pageContent = (shown: SurveyPage) => {
        // Only One section per page pages carry a heading, and a language without one shows none.
        const heading = localize(shown.sectionTitle);
        const titled = !!heading.trim();

        return (
            <>
                {titled && <SurveyRunnerSectionHeading title={heading}/>}
                {shown.questions.map((question, position) => card(question, position, titled ? 3 : 2))}
            </>
        );
    };

    const welcomeHeading = !!welcome && (
        <Stack gap={1}>
            <SurveyRunnerScreenTitle title={localize(welcome.title)}/>
            {!!localize(welcome.description) && (
                <SurveyRunnerMarkdown markdown={localize(welcome.description)} fontSize={16} color={'text.secondary'} align={'center'}/>
            )}
        </Stack>
    );

    const welcomePage = !!welcome && (
        <Stack gap={2} alignItems={'center'} sx={{textAlign: 'center', py: {xs: 2, sm: 4}}}>
            {welcomeHeading}
            <Button
                variant={'contained'}
                size={'large'}
                disableElevation
                onClick={() => setStarted(true)}
                sx={{mt: 1, minHeight: 48, minWidth: 200}}
            >
                {localize(welcome.buttonLabel) || t('Start')}
            </Button>
        </Stack>
    );

    const endPage = (
        <Stack gap={1} sx={{textAlign: 'center', py: {xs: 4, sm: 6}}}>
            <SurveySuccessAnimation/>

            <SurveyRunnerScreenTitle title={localize(endScreen?.title) || t('Thank you')}/>
            {!!localize(endScreen?.description) && (
                <SurveyRunnerMarkdown markdown={localize(endScreen?.description)} fontSize={16} color={'text.secondary'} align={'center'}/>
            )}

            <Button
                variant={'text'}
                size={'small'}
                onClick={() => setFinished(false)}
                sx={{alignSelf: 'center', mt: 1}}
            >
                {tEditor('Back')}
            </Button>
        </Stack>
    );

    const emptyState = (
        <Typography variant={'body2'} color={'text.secondary'}>
            {tEditor('No questions yet - open the editor to build the form.')}
        </Typography>
    );

    const pagedStep = (
        <>
            {page ? pageContent(page) : emptyState}

            <Stack direction={'row'} gap={2} justifyContent={'space-between'} alignItems={'center'}>
                <Button
                    variant={'outlined'}
                    size={'large'}
                    disableElevation
                    disabled={pageIndex === 0 && !welcome}
                    onClick={() => void goBack()}
                    sx={{minHeight: 48}}
                >
                    {t('Back')}
                </Button>

                <Button
                    variant={'contained'}
                    size={'large'}
                    disableElevation
                    onClick={() => void advance()}
                    sx={{minHeight: 48, minWidth: 140}}
                >
                    {onLastPage ? submitLabel : t('Continue')}
                </Button>
            </Stack>
        </>
    );

    const singlePage = (
        <>
            {!!welcome && (
                <SurveyRunnerIntroCard
                    title={localize(welcome.title)}
                    description={localize(welcome.description)}
                />
            )}

            {page ? pageContent(page) : emptyState}

            <Button
                variant={'contained'}
                size={'large'}
                disableElevation
                onClick={() => void advance()}
                sx={{alignSelf: 'flex-start', minHeight: 48}}
            >
                {submitLabel}
            </Button>
        </>
    );

    const content = finished
        ? endPage
        : paged
            ? (onWelcomeScreen ? welcomePage : pagedStep)
            : singlePage;

    const answeredShare = page?.questions.length
        ? page.questions.filter(question => isAnswered(answers[idOf(question)])).length / page.questions.length
        : 0;

    const footer = (
        <PublicSurveyFooter
            survey={survey}
            localize={localize}
            language={activeLanguage}
            organizationName={organizationName}
            languages={survey.languages}
            onLanguageChange={onLanguageChange}
        />
    );

    return (
        <>
            <Box ref={topRef} aria-hidden/>

            <SurveyRunnerFrame
                theme={survey.theme}
                reserveProgressSpace={paged}
                /* Pages as on the guest page, but all of them, like the questions; the current one fills as it is answered. */
                progress={paged && !onWelcomeScreen && pages.length > 0
                    ? surveyProgress(pages.length, pageIndex, answeredShare, finished)
                    : undefined}
                language={activeLanguage}
                progressLabel={t('Survey progress')}
                showLogo={!paged || finished || onWelcomeScreen}
                footer={footer}
            >
                {content}
            </SurveyRunnerFrame>
        </>
    );
}

export default SurveyRunner;
